import type {ComponentProps} from 'react';
import gql from 'graphql-tag';
import {EzNavigation} from '@ezcater/recipe';
import {NavLink, useHistory} from 'react-router-dom';
import {useCurrentAccount} from 'components/CurrentAccount';
import {useMutation} from 'services/apollo';
import DestroyDriverSession from 'services/graphql/DestroyDriverSession';
import {useFlashMessages} from '@ezcater/flash-messages';
import {
  type DestroyDriverSessionMutation,
  type DestroyDriverSessionMutationVariables,
  type HeaderSessionDestroyMutation,
  type HeaderSessionDestroyMutationVariables,
} from 'services/graphql/types';
import {useFederationApiEppoFlag} from 'services/eppo';
import {REQUEST_SIGN_IN_LINK_PATH} from 'paths';

export const SessionDestroyMutation = gql(/* GraphQL */ `
  mutation HeaderSessionDestroyMutation($input: DriverAccountSessionDestroyInput! = {}) {
    destroyDriverSession: driverAccountSessionDestroy(input: $input) {
      userErrors {
        ... on UserError {
          message
        }
      }
    }
  }
`);

type UseSessionDestroyMutationOptions = {
  onError: () => void;
};

type UseSessionDestroyMutationResult =
  | ReturnType<
      typeof useMutation<DestroyDriverSessionMutation, DestroyDriverSessionMutationVariables>
    >
  | ReturnType<
      typeof useMutation<HeaderSessionDestroyMutation, HeaderSessionDestroyMutationVariables>
    >;

const useSessionDestroyMutation = (
  options: UseSessionDestroyMutationOptions
): UseSessionDestroyMutationResult => {
  const featureFlag = useFederationApiEppoFlag();
  const [executeLegacyMutation, legacyMutationResult] = useMutation<
    DestroyDriverSessionMutation,
    DestroyDriverSessionMutationVariables
  >(DestroyDriverSession, {...options, variables: {input: {}}});
  const [executeMutation, mutationResult] = useMutation<
    HeaderSessionDestroyMutation,
    HeaderSessionDestroyMutationVariables
  >(SessionDestroyMutation, options);

  if (featureFlag) {
    return [executeMutation, mutationResult];
  }

  return [executeLegacyMutation, legacyMutationResult];
};

interface HeaderProps {
  children: React.ReactNode;
  orderNumber?: string;
}

const Header = ({children, orderNumber}: HeaderProps) => {
  const history = useHistory();

  const handleError = () => {
    addFlashMessage({
      type: 'error',
      message: 'There was an error logging you out.  Please try again.',
    });
  };

  const [signOut] = useSessionDestroyMutation({
    onError: handleError,
  });
  const {addFlashMessage} = useFlashMessages();
  const helpLink = orderNumber ? `/help/${orderNumber.replace(/[^0-9a-zA-Z]/g, '')}` : '/help';
  const {id} = useCurrentAccount();
  const signOutClick = async () => {
    const response = await signOut();

    if (response?.data?.destroyDriverSession) {
      if (response.data.destroyDriverSession.userErrors.length) {
        handleError();
      } else {
        history.push(REQUEST_SIGN_IN_LINK_PATH);
      }
    }
  };

  const helpElement = {to: helpLink, label: 'Help', as: NavLink};
  const getNavLinks = (): ComponentProps<typeof EzNavigation>['links'] => {
    if (id) {
      return [
        {to: '/deliveries', label: 'My Deliveries', as: NavLink},
        {to: helpLink, label: 'Help', as: NavLink},
        {href: '#', label: 'Sign Out', onClick: signOutClick},
      ];
    }

    return [helpElement, {to: REQUEST_SIGN_IN_LINK_PATH, label: 'Sign In', as: NavLink}];
  };

  return (
    <EzNavigation
      home={{
        to: '#', // TODO: Change to deliveries page
        label: 'ezCater',
        logo: {src: '/static/images/brand/logo-light.png', width: 100},
        as: NavLink,
      }}
      links={getNavLinks()}
    >
      {children}
    </EzNavigation>
  );
};

export default Header;
